
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import ApiResponse from 'models/api-response/api-response.model';
import Log from 'models/log/log.model';
import Project from 'models/project/project.model';
import TicketAttribute from 'models/ticket-attribute/ticket-attribute.model';
import { ApiRequestService } from 'services/api-request/api-request.service';
import { LogService } from 'services/log/log.service';

@Injectable({
  providedIn: 'root',
})
export class TicketAttributeService {
  private selectedInstance: BehaviorSubject<TicketAttribute> = new BehaviorSubject(new TicketAttribute());
  private selectedList: BehaviorSubject<TicketAttribute[]> = new BehaviorSubject([]);

  constructor(
    public apiRequestService: ApiRequestService,
    public logService: LogService,
  ) { }

  public async create(instance: TicketAttribute): Promise<TicketAttribute> {
    return new Promise((resolve: (r: TicketAttribute) => any, reject: (e: Error) => any) => {
        this.apiRequestService.post('/ticketattribute/create', {
            name: instance.name,
            project: instance.project.id,
            possibleValues: instance.possibleValues,
            otherEnabled: instance.otherEnabled,
            multi: instance.multi,
            isDate: instance.isDate,
            auto: instance.auto,
            searchable: instance.searchable,
            ticketAttributeExtensions: instance.ticketAttributeExtensions,
            isArcgis: instance.isArcgis,
        }).subscribe({
            next: async (res: ApiResponse<TicketAttribute>) => {
                if (!res.success) {
                    await this.logService.create(new Log({
                        description: 'requestError: ' + JSON.stringify(res),
                        event: 'serviceError',
                        performer: 'TicketAttributeService.create',
                        type: 'error',
                      }));
                    return reject(new Error('requestError'));
                }
                return resolve(new TicketAttribute(res.data));
            },
            error: async error => {
              await this.logService.create(new Log({
                description: error,
                event: 'serviceError',
                performer: 'TicketAttributeService.create',
                type: 'error'
              }));
              return reject(new Error('requestError'));
            }
          });
    });
  }

  public async get(id: string): Promise<TicketAttribute> {
    return new Promise((resolve: (r: TicketAttribute) => any, reject: (e: Error) => any) => {
        this.apiRequestService.get('/ticketattribute/get/' + id).subscribe({
          next: async (res: ApiResponse<TicketAttribute>) => {
            if (!res.success) {
                await this.logService.create(new Log({
                    description: 'requestError: ' + JSON.stringify(res),
                    event: 'serviceError',
                    performer: 'TicketAttributeService.get',
                    type: 'error'
                  }));
                return reject(new Error('requestError'));
            }
            return resolve(new TicketAttribute(res.data));
          },
          error: async error => {
            console.log(error);
            await this.logService.create(new Log({
              description: error,
              event: 'serviceError',
              performer: 'TicketAttributeService.get',
              type: 'error'
            }));
            return reject(new Error('requestError'));
          }
        });
    });
  }

  public async list(project: Project): Promise<TicketAttribute[]> {
    return new Promise((resolve: (r: TicketAttribute[]) => any, reject: (e: Error) => any) => {
        this.apiRequestService.get('/ticketattribute/list/' + project.id).subscribe({
          next: async (res: ApiResponse<TicketAttribute[]>) => {
            if (!res.success) {
                await this.logService.create(new Log({
                    description: 'requestError: ' + JSON.stringify(res),
                    event: 'serviceError',
                    performer: 'TicketAttributeService.get',
                    type: 'error'
                  }));
                return reject(new Error('requestError'));
            }
            const arr: TicketAttribute[] = [];
            for (let i = 0; i < res.data.length; i++) {
              arr.push(new TicketAttribute(res.data[i]));
            }
            return resolve(arr);
          },
          error: async error => {
            console.log(error);
            await this.logService.create(new Log({
              description: error,
              event: 'serviceError',
              performer: 'TicketAttributeService.get',
              type: 'error'
            }));
            return reject(new Error('requestError'));
          }
        });
    });
  }

  public async selectList(project: Project): Promise<TicketAttribute[]> {
    return new Promise(async (resolve: (r: TicketAttribute[]) => any, reject: (e: Error) => any) => {
      const res = await this.list(project);
      this.selectedList.next(res);
      return resolve(res);
    });
  }

  public async update(instance: TicketAttribute): Promise<TicketAttribute> {
    return new Promise((resolve: (r: TicketAttribute) => any, reject: (e: Error) => any) => {
        this.apiRequestService.post('/ticketattribute/update/', {
          id: instance.id,
          name: instance.name,
          possibleValues: instance.possibleValues,
          otherEnabled: instance.otherEnabled,
          multi: instance.multi,
          isDate: instance.isDate,
          auto: instance.auto,
          searchable: instance.searchable,
          ticketAttributeExtensions: instance.ticketAttributeExtensions,
          isArcgis: instance.isArcgis,
      }).subscribe({
          next: async (res: ApiResponse<TicketAttribute>) => {
              if (!res.success) {
                  await this.logService.create(new Log({
                      description: 'requestError: ' + JSON.stringify(res),
                      event: 'serviceError',
                      performer: 'TicketAttributeService.update',
                      type: 'error'
                    }));
                  return reject(new Error('requestError'));
              }
              return resolve(new TicketAttribute(res.data));
          },
          error: async error => {
            console.log(error);
            await this.logService.create(new Log({
              description: error,
              event: 'serviceError',
              performer: 'TicketAttributeService.update',
              type: 'error'
            }));
            return reject(new Error('requestError'));
          }
        });
    });
  }

  public async delete(instance: TicketAttribute): Promise<boolean> {
    return new Promise((resolve: (r: boolean) => any, reject: (e: Error) => any) => {
        this.apiRequestService.delete('/ticketattribute/' + instance.id, {}).subscribe({
          next: async (res: ApiResponse<boolean>) => {
              if (!res.success) {
                  await this.logService.create(new Log({
                      description: 'requestError: ' + JSON.stringify(res),
                      event: 'serviceError',
                      performer: 'TicketAttributeService.delete',
                      type: 'error'
                    }));
                  return reject(new Error('requestError'));
              }
              return resolve(res.data);
          },
          error: async error => {
            console.log(error);
            await this.logService.create(new Log({
              description: error,
              event: 'serviceError',
              performer: 'TicketAttributeService.delete',
              type: 'error'
            }));
            return reject(new Error('requestError'));
          }
        });
    });
  }

  public getSelectedInstance() {
    return this.selectedInstance;
  }

  public getSelectedList() {
    return this.selectedList;
  }
}
